import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import classNames from "classnames";
import FileSelector from "../../../files/composer/FileSelector";
import { FileData } from "../../../../@types/files";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/pro-regular-svg-icons";
import { useParams } from "../../../../helpers/tixxt-router";
import { find } from "lodash";
import { useFetchFolderMetadata } from "../../../files/api";
import Bridge from "../../../../turbo/bridge";
import extensionAllowed from "../../../../helpers/files/extensionAllowed";
import { acceptsFile } from "../../../../helpers/files/imageAccept";

type FileInputArgs = {
  onSelectFile: (e: any) => void;
  multiple?: boolean;
  acceptedType: string;
  empty: boolean;
  isImage?: boolean;
  selectExisting?: boolean;
  onSelectExistingFile: (selectedFiles: FileData[]) => void;
  onDropFile: ({ files }: { files: any }) => void;
  storageDirectory: string;
  disabled?: boolean;
};

function getAcceptedFileTypes(
  storageDirectory: string,
  acceptedType: string,
): string {
  // Same rule as extensionAllowed: only the filter itself counts, not whether
  // the setting is still offered in the administration (tixxt/core#258).
  if (
    storageDirectory !== "files" ||
    !Preload.current_network.files.extension_filter_enabled
  ) {
    return acceptedType;
  }

  // Never build an extension-based accept list inside the Android app: the
  // native file chooser (turbo-android) uses the first accept entry verbatim
  // as the intent MIME type, and ".pdf" & co. resolve to no activity — the
  // picker then fails with "no app can perform this action" (#112). The
  // selection is still validated below (validateFile) and by the backend.
  if (window.isApp && Bridge.isAndroid()) {
    return acceptedType;
  }

  const filterType = Preload.current_network.files.extension_filter_type;
  const filterList = Preload.current_network.files.extension_filter_list;

  // Currently, only `*` is passed as `acceptedType` for `storageDirectory === ‘files’`,
  // so we can simply replace this here. Should this change in the future,
  // we will have to consider how to reconcile the `filterList` and the `acceptedType`.
  if (filterType === "allow" && Array.isArray(filterList)) {
    return "." + filterList.join(",.");
  }

  return acceptedType;
}

function validateFile(
  file: File,
  storageDirectory: string,
  acceptedType: string,
): boolean {
  // Images and assets: the same rule as the file dialog, which a drop
  // bypasses (tixxt/core#694)
  if (storageDirectory !== "files") return acceptsFile(acceptedType, file);

  return extensionAllowed(file.name.match(/\.(\w+)$/)?.[1]);
}

function filterValidFiles(
  allFiles: File[],
  storageDirectory: string,
  acceptedType: string,
): File[] {
  const invalidFiles = allFiles.filter(
    (file) => !validateFile(file, storageDirectory, acceptedType),
  );

  if (invalidFiles.length > 0 && storageDirectory === "images") {
    toastr.error(I18n.t("js.files.uploader.error_image_type"));
  } else if (invalidFiles.length > 0 && storageDirectory !== "files") {
    // assets differ per field (SVG only, .png/.ico, …), so no format list
    toastr.error(
      I18n.t("errors.uploader.file_type_not_accepted", {
        filename: invalidFiles.map((file) => file.name).join(", "),
      }),
    );
  } else if (invalidFiles.length > 0) {
    const filterType = Preload.current_network.files.extension_filter_type;
    if (filterType === "allow") {
      const filterList = Preload.current_network.files.extension_filter_list;
      toastr.error(
        I18n.t("errors.uploader.file_type_not_accepted_allow_list", {
          filename: invalidFiles.map((file) => file.name).join(", "),
          allowedExtensions: "." + filterList.join(", ."),
        }),
      );
    } else {
      toastr.error(
        I18n.t("errors.uploader.file_type_not_accepted", {
          filename: invalidFiles.map((file) => file.name).join(", "),
        }),
      );
    }
  }

  return allFiles.filter((file) => !invalidFiles.includes(file));
}

function createFileSelectHandler(
  originalHandler: (e: any) => void,
  storageDirectory: string,
  acceptedType: string,
) {
  return (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const validFiles = filterValidFiles(
        Array.from(e.target.files),
        storageDirectory,
        acceptedType,
      );

      if (validFiles.length === 0) {
        return;
      }

      const dataTransfer = new DataTransfer();
      validFiles.forEach((file) => dataTransfer.items.add(file));

      e.target.files = dataTransfer.files;
    }

    originalHandler(e);
  };
}

function FileInput({
  onSelectFile,
  multiple,
  acceptedType,
  empty,
  isImage = false,
  selectExisting,
  onSelectExistingFile,
  onDropFile,
  disabled = false,
  storageDirectory,
}: FileInputArgs) {
  const [isOver, setIsOver] = useState(false);
  const dragCounter = useRef(0);
  const params = useParams();
  const groupSlug = params.groupSlug || params.group_slug;

  const effectiveAcceptedType = getAcceptedFileTypes(
    storageDirectory,
    acceptedType,
  );

  const handleSelectFile = createFileSelectHandler(
    onSelectFile,
    storageDirectory,
    effectiveAcceptedType,
  );

  const currentRootFolderId = groupSlug
    ? find(Preload.my_groups, { slug: groupSlug })?.root_folder_id
    : Preload.current_network.root_folder_id;

  const { data: currentRootFolder } = useFetchFolderMetadata(
    currentRootFolderId,
    "folders",
  );

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setIsOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsOver(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsOver(false);
    const validFiles = filterValidFiles(
      Array.from(e.dataTransfer.files),
      storageDirectory,
      effectiveAcceptedType,
    );
    if (validFiles.length > 0) {
      onDropFile({ files: validFiles });
    }
  };

  return multiple || (!multiple && empty) ? (
    <div
      className="filesInput flex-1"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        className={classNames(
          "flex flex-col gap-2 h-full items-center justify-center min-h-[100px] uploader-dropzone",
          { isOver },
        )}
      >
        <p className={classNames("font-medium", { "text-muted": !!disabled })}>
          {I18n.t(
            `js.files.uploader.drag_and_drop_${
              isImage ? (multiple ? "images" : "image") : "files"
            }`,
          )}
        </p>
        <label
          className={classNames(
            "fileInputButton btn btn-light btn-sm fileFocusButton",
            { disabled: !!disabled },
          )}
        >
          <FontAwesomeIcon icon={faUpload} />{" "}
          {I18n.t(
            `js.files.uploader.select_${isImage ? "image" : "file"}${
              multiple ? "s" : ""
            }`,
          )}
          <input
            type="file"
            onChange={handleSelectFile}
            multiple={multiple}
            accept={effectiveAcceptedType}
            style={{ width: "1px", opacity: 0 }}
            disabled={disabled || (!multiple && !empty)}
            aria-hidden
          />
        </label>
        {selectExisting ? (
          <FileSelector
            currentRootFolder={currentRootFolder}
            onSelectFiles={onSelectExistingFile}
            disabled={disabled}
          />
        ) : null}
      </div>
    </div>
  ) : (
    <div className="filesInput">
      <div className="filesDropTarget">
        <p>
          {I18n.t(
            `js.files.uploader.remove_${isImage ? "image" : "file"}_hint`,
          )}
        </p>
      </div>
    </div>
  );
}

export default FileInput;
